import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type {
  RepairshoprTicketLineItem,
  LineItemOverride,
  LineItemSyncResponse,
  WorkItemInvoiceSummary,
} from '@techgitter/contracts';
import type { LineItem } from '../types/ticketTypeTemplate';
import {
  syncJobLineItems,
  patchLineItemOverrides,
  createRsLineItem,
  updateRsLineItem,
  deleteRsLineItem,
  fetchJobInvoices,
} from '../services/jobLineItemsSyncService';
import { setLineItemsSyncRanAt } from '../services/lineItemsSyncStatus';
import { getRsDirtyFields, buildOverrides, buildLineItemCreate } from '../utils/lineItemEditDiff';
import { hydratedUnitCost } from '../utils/nonChargeablePrice';
import { allocateLicenseKey, fetchAvailableLicenseKeys } from '../services/licenseKeysApi';

interface UseWorkItemLineItemsOptions {
  jobGuid: string;
  /** Called when the hook needs a sync (e.g. after creating items). Wire to wrappedOnSync for echo suppression. */
  onSyncRequested?: () => void;
  rsLineItems?: RepairshoprTicketLineItem[];
  lineItemOverrides?: LineItemOverride[];
  lineItemsSyncedAt?: string;
  invoices?: WorkItemInvoiceSummary[];
  onSynced?: (response: LineItemSyncResponse) => void;
}

/**
 * Merge RS line items + DV overrides + invoice data → UI LineItem[].
 * Converted ticket items are replaced by invoice line items (which may
 * have different pricing, quantities, or additional items).
 */
function hydrateFromRS(
  rsItems: RepairshoprTicketLineItem[],
  overrides: LineItemOverride[],
  invoices?: WorkItemInvoiceSummary[],
): LineItem[] {
  const overrideMap = new Map(overrides.map(o => [o.rsLineItemId, o]));

  // Unconverted ticket items — editable
  const unconverted: LineItem[] = rsItems
    .filter(item => !item.converted)
    .map((item, idx) => {
      const override = overrideMap.get(item.id);
      const quantity = parseFloat(item.quantity) || 0;
      const unitCost = hydratedUnitCost(item.price_retail, override);
      const nonChargeable = override?.nonChargeable ?? false;
      const subtotal = quantity * unitCost;

      return {
        // Items written at job-create time have no RS id yet (the
        // orchestrator assigns ids on the first sync). Fall back to a
        // stable index-based key so multiple id-less rows don't collide on
        // the React key and silently drop from the list.
        id: item.id != null ? String(item.id) : `rs-noid-${idx}`,
        source: 'rs' as const,
        rsLineItemId: item.id,
        converted: false,
        lineItemId: item.name,
        // Carried through so this panel's save doesn't blank the key when it
        // rewrites the metadata blob — the blob is replaced wholesale, and the
        // key is what stops the Edit Ticket page duplicating the line later.
        templateKey: override?.templateKey,
        category: override?.category ?? '',
        quantity,
        unitCost,
        prefixUserBase: override?.prefixUserBase ?? '',
        afterHours: override?.afterHours ?? false,
        nonChargeable,
        lineDetail: item.description,
        subtotal,
        finalTotal: nonChargeable ? 0 : subtotal,
        overrideState: undefined,
      };
    });

  // Invoice line items — read-only, shown instead of converted ticket items
  const invoiced: LineItem[] = [];
  if (invoices) {
    for (const inv of invoices) {
      if (!inv.line_items) continue;
      for (let idx = 0; idx < inv.line_items.length; idx++) {
        const li = inv.line_items[idx];
        const quantity = parseFloat(String(li.quantity)) || 0;
        const unitCost = parseFloat(String(li.price)) || 0;
        const subtotal = quantity * unitCost;

        invoiced.push({
          id: `inv-${inv.id}-${idx}`,
          source: 'rs' as const,
          converted: true,
          lineItemId: li.description || li.name,
          category: '',
          quantity,
          unitCost,
          prefixUserBase: '',
          afterHours: false,
          nonChargeable: false,
          lineDetail: li.name,
          subtotal,
          finalTotal: subtotal,
          overrideState: undefined,
        });
      }
    }
  }

  return [...invoiced, ...unconverted];
}


function createEmptyLineItem(): LineItem {
  return {
    id: crypto.randomUUID(),
    source: 'manual',
    lineItemId: '',
    category: '',
    quantity: 1,
    unitCost: 0,
    prefixUserBase: '',
    afterHours: false,
    nonChargeable: false,
    lineDetail: '',
    subtotal: 0,
    finalTotal: 0,
  };
}

export function useWorkItemLineItems({
  jobGuid,
  onSyncRequested,
  rsLineItems: initialRsItems,
  lineItemOverrides: initialOverrides,
  lineItemsSyncedAt: initialSyncedAt,
  invoices: initialInvoices,
  onSynced,
}: UseWorkItemLineItemsOptions) {
  const [rsItems, setRsItems] = useState<RepairshoprTicketLineItem[]>(
    initialRsItems ?? [],
  );
  const hasRsData = rsItems.length > 0;

  const [lineItems, setLineItems] = useState<LineItem[]>(() =>
    initialRsItems && initialRsItems.length > 0
      ? hydrateFromRS(initialRsItems, initialOverrides ?? [], initialInvoices)
      : [],
  );
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrorItemId, setValidationErrorItemId] = useState<string | null>(null);

  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(
    initialSyncedAt ?? null,
  );

  // Track last-seen prop references to detect external updates (SignalR → parent → props)
  const prevRsItemsRef = useRef(initialRsItems);
  const prevInvoicesRef = useRef(initialInvoices);

  // Keep invoices ref current so callbacks (onSync, reconcileRsData) always see
  // the latest value without needing it in their dependency arrays.
  const invoicesRef = useRef(initialInvoices);
  useEffect(() => { invoicesRef.current = initialInvoices; }, [initialInvoices]);

  // Track optimistically deleted RS line item IDs so re-hydration doesn't resurrect them.
  // Entries are cleared once the incoming rsLineItems no longer contain the deleted ID
  // (confirming the delete has propagated).
  const deletedRsIdsRef = useRef<Set<number>>(new Set());

  // React to parent prop changes (rsLineItems, overrides, invoices)
  useEffect(() => {
    const rsChanged = initialRsItems !== prevRsItemsRef.current;
    const invoicesChanged = initialInvoices !== prevInvoicesRef.current;
    if (!rsChanged && !invoicesChanged) return;
    prevRsItemsRef.current = initialRsItems;
    prevInvoicesRef.current = initialInvoices;

    if (!initialRsItems || initialRsItems.length === 0) return;

    // Clear confirmed deletes — if the incoming data no longer contains the ID, the delete propagated
    const incomingIds = new Set(initialRsItems.map(r => r.id));
    deletedRsIdsRef.current.forEach(deletedId => {
      if (!incomingIds.has(deletedId)) {
        deletedRsIdsRef.current.delete(deletedId);
      }
    });

    const filtered = initialRsItems.filter(r => !deletedRsIdsRef.current.has(r.id));
    const hydrated = hydrateFromRS(filtered, initialOverrides ?? [], initialInvoices);

    // Merge: use RS data as truth, but preserve pending items that aren't yet in RS.
    // Match pending → RS by lineItemId (SKU). If the SKU now appears in RS, the item
    // has been confirmed and the pending copy can be dropped.
    setLineItems(prev => {
      const pendingItems = prev.filter(item => item.pending);
      if (pendingItems.length === 0) return hydrated;

      const rsSkuCounts = new Map<string, number>();
      for (const h of hydrated) {
        rsSkuCounts.set(h.lineItemId, (rsSkuCounts.get(h.lineItemId) ?? 0) + 1);
      }
      // Consume one RS "slot" per pending item with matching SKU
      const surviving: LineItem[] = [];
      for (const p of pendingItems) {
        const count = rsSkuCounts.get(p.lineItemId) ?? 0;
        if (count > 0) {
          // RS now has this SKU — pending item confirmed, drop it
          rsSkuCounts.set(p.lineItemId, count - 1);
        } else {
          // Not yet in RS — keep as pending
          surviving.push(p);
        }
      }
      return [...hydrated, ...surviving];
    });

    setRsItems(initialRsItems);
    if (initialSyncedAt) setLastSyncedAt(initialSyncedAt);
  }, [initialRsItems, initialOverrides, initialSyncedAt, initialInvoices]);

  const total = useMemo(
    () => lineItems.reduce((sum, li) => sum + li.finalTotal, 0),
    [lineItems],
  );

  const onAddLineItem = useCallback(() => {
    const newItem = createEmptyLineItem();
    setLineItems(prev => [...prev, newItem]);
    setExpandedItems(prev => ({ ...prev, [newItem.id]: true }));
    setIsDirty(true);
  }, []);

  const onUpdateLineItem = useCallback((id: string, updates: Partial<LineItem>) => {
    setLineItems(prev =>
      prev.map(item => (item.id === id ? { ...item, ...updates } : item)),
    );
    setIsDirty(true);
    if (validationErrorItemId === id) setValidationErrorItemId(null);
  }, [validationErrorItemId]);

  const onDeleteLineItem = useCallback((id: string) => {
    const item = lineItems.find(li => li.id === id);

    // Optimistic removal from local state
    setLineItems(prev => prev.filter(li => li.id !== id));
    setExpandedItems(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    if (item?.source === 'rs' && item.rsLineItemId != null) {
      // Track the deleted ID so re-hydration from SignalR doesn't resurrect it
      deletedRsIdsRef.current.add(item.rsLineItemId);

      // Delete from RepairShopr + Dataverse via API
      deleteRsLineItem(jobGuid, item.rsLineItemId).catch(err => {
        console.error('[useWorkItemLineItems] deleteRsLineItem FAILED — reverting', err);
        deletedRsIdsRef.current.delete(item.rsLineItemId!);
        // Revert on failure
        setLineItems(prev => [...prev, item].sort((a, b) => a.id.localeCompare(b.id)));
      });
    } else {
      setIsDirty(true);
    }
  }, [lineItems, jobGuid]);

  const onExpandChange = useCallback((id: string, expanded: boolean) => {
    setExpandedItems(prev => ({ ...prev, [id]: expanded }));
  }, []);

  const onSync = useCallback(async () => {
    setIsSyncing(true);
    setSyncError(null);
    try {
      // Fetch line items and invoices in parallel so hydration always has
      // up-to-date invoice data (fixes stale-closure bug after invoice creation).
      const [response, invoiceResult] = await Promise.all([
        syncJobLineItems(jobGuid),
        fetchJobInvoices(jobGuid).catch(() => null),
      ]);
      setRsItems(response.lineItems);
      setLastSyncedAt(response.syncedAt);
      // A manual sync is also a "check" — surface it in the global Last-check
      // badge so users get immediate feedback without waiting for the next cron.
      setLineItemsSyncRanAt(response.syncedAt);
      const freshInvoices = (invoiceResult?.invoices as WorkItemInvoiceSummary[] | null)
        ?? invoicesRef.current;
      setLineItems(hydrateFromRS(response.lineItems, response.overrides, freshInvoices));
      setIsDirty(false);
      onSynced?.(response);
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Failed to sync line items');
    } finally {
      setIsSyncing(false);
    }
  }, [jobGuid, onSynced]);

  /**
   * Accept RS line item data directly (e.g. from a SignalR payload) without an API call.
   * Uses RS data as truth but preserves pending items not yet confirmed in RS.
   */
  const reconcileRsData = useCallback((
    incomingRsItems: RepairshoprTicketLineItem[],
    incomingOverrides: LineItemOverride[],
  ) => {
    const filtered = incomingRsItems.filter(r => !deletedRsIdsRef.current.has(r.id));
    const hydrated = hydrateFromRS(filtered, incomingOverrides, invoicesRef.current);

    setLineItems(prev => {
      const pendingItems = prev.filter(item => item.pending);
      if (pendingItems.length === 0) return hydrated;

      const rsSkuCounts = new Map<string, number>();
      for (const h of hydrated) {
        rsSkuCounts.set(h.lineItemId, (rsSkuCounts.get(h.lineItemId) ?? 0) + 1);
      }
      const surviving: LineItem[] = [];
      for (const p of pendingItems) {
        const count = rsSkuCounts.get(p.lineItemId) ?? 0;
        if (count > 0) {
          rsSkuCounts.set(p.lineItemId, count - 1);
        } else {
          surviving.push(p);
        }
      }
      return [...hydrated, ...surviving];
    });

    setRsItems(incomingRsItems);
    setIsDirty(false);
  }, []);

  // Snapshot maps for dirty detection — built from the last-known RS state
  const rsSnapshot = useMemo(
    () => new Map(rsItems.map(r => [r.id, r])),
    [rsItems],
  );
  // Auto-sync after creating/updating RS items. The orchestrator typically
  // processes within a few seconds — schedule a sync to pick up confirmed data.
  const pendingSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const syncFn = onSyncRequested ?? onSync;
  const schedulePendingSync = useCallback(() => {
    if (pendingSyncTimerRef.current) clearTimeout(pendingSyncTimerRef.current);
    pendingSyncTimerRef.current = setTimeout(() => {
      pendingSyncTimerRef.current = null;
      syncFn();
    }, 3000);
  }, [syncFn]);

  /**
   * Provision a license key against a license-key line item: allocate one
   * available key (oldest in stock) to this ticket's customer, then write the
   * key into the line's detail (RS description) and persist it. Allocates
   * exactly one key per line regardless of quantity. Irreversible from the UI.
   */
  const provisionLicense = useCallback(
    async (id: string, customerId: string) => {
      const line = lineItems.find((i) => i.id === id);
      if (!line) throw new Error('Line item not found');
      if (!customerId) throw new Error('This ticket has no customer to allocate to');
      const sku = line.lineItemId;
      if (!sku) throw new Error('Choose a product on this line first');

      const available = await fetchAvailableLicenseKeys({ sku });
      if (available.length === 0) {
        throw new Error(
          `No "${sku}" keys in stock — buy more via Part Orders and receive them.`,
        );
      }
      const allocated = await allocateLicenseKey(available[0].id, {
        customerId,
        jobId: jobGuid,
      });

      const existing = (line.lineDetail || '').trim();
      const newDetail = existing
        ? `${existing}\nLicense Key: ${allocated.key}`
        : `License Key: ${allocated.key}`;

      // Persist the key into the line's RS description — patch an existing RS
      // line, or create one for a not-yet-saved manual line (mirrors onSave).
      if (line.rsLineItemId != null) {
        await updateRsLineItem(jobGuid, line.rsLineItemId, { description: newDetail });
      } else {
        await createRsLineItem(jobGuid, {
          ...buildLineItemCreate(line),
          description: newDetail,
        });
      }

      setLineItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, lineDetail: newDetail, pending: true } : i)),
      );
      schedulePendingSync();
    },
    [lineItems, jobGuid, schedulePendingSync],
  );

  const onSave = useCallback(async () => {
    setValidationErrorItemId(null);

    // Validate: every non-converted item must have quantity >= 0.1
    const invalidItem = lineItems.find(
      item => !item.converted && !item.pending && item.quantity < 0.1,
    );
    if (invalidItem) {
      setValidationErrorItemId(invalidItem.id);
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      if (hasRsData) {
        // POST any new manual items to RS
        const newManualItems = lineItems.filter(
          item => item.source === 'manual' && item.rsLineItemId == null && !item.pending,
        );

        // PATCH any RS-sourced items with dirty RS fields (quantity, price, description)
        const dirtyRsUpdates: { item: LineItem; fields: NonNullable<ReturnType<typeof getRsDirtyFields>> }[] = [];
        for (const item of lineItems) {
          if (item.source === 'rs' && item.rsLineItemId != null && !item.pending) {
            const dirty = getRsDirtyFields(item, rsSnapshot);
            if (dirty) {
              dirtyRsUpdates.push({ item, fields: dirty });
            }
          }
        }

        // Fire creates and updates in parallel
        await Promise.all([
          ...newManualItems.map(item =>
            createRsLineItem(jobGuid, buildLineItemCreate(item)),
          ),
          ...dirtyRsUpdates.map(({ item, fields }) =>
            updateRsLineItem(jobGuid, item.rsLineItemId!, fields),
          ),
        ]);

        // Mark created + updated items as pending
        const pendingIds = new Set([
          ...newManualItems.map(i => i.id),
          ...dirtyRsUpdates.map(({ item }) => item.id),
        ]);
        if (pendingIds.size > 0) {
          setLineItems(prev =>
            prev.map(item =>
              pendingIds.has(item.id) ? { ...item, pending: true } : item,
            ),
          );
          // Schedule a sync to pick up confirmed RS data after orchestrator processes
          schedulePendingSync();
        }

        // Save overrides (SM4RT metadata) — these are synchronous to Dataverse
        await patchLineItemOverrides(jobGuid, buildOverrides(lineItems));
      } else {
        // No RS data yet — send all non-empty items through the orchestrator
        const newItems = lineItems.filter(
          item => item.lineItemId && !item.pending,
        );

        await Promise.all(
          newItems.map(item =>
            createRsLineItem(jobGuid, buildLineItemCreate(item)),
          ),
        );

        if (newItems.length > 0) {
          const pendingIds = new Set(newItems.map(i => i.id));
          setLineItems(prev =>
            prev.map(item =>
              pendingIds.has(item.id) ? { ...item, pending: true } : item,
            ),
          );
          // Don't schedulePendingSync here — the orchestrator will broadcast
          // jobs:line-items-synced via SignalR when it processes the create,
          // which triggers onSync in the panel's listener.
        }
      }
      setIsDirty(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save line items');
    } finally {
      setIsSaving(false);
    }
  }, [lineItems, jobGuid, hasRsData, rsSnapshot, schedulePendingSync]);

  return {
    lineItems,
    expandedItems,
    total,
    isDirty,
    isSaving,
    saveError,
    onAddLineItem,
    onUpdateLineItem,
    onDeleteLineItem,
    onExpandChange,
    onSave,
    provisionLicense,
    isSyncing,
    syncError,
    lastSyncedAt,
    onSync,
    reconcileRsData,
    hasRsData,
    validationErrorItemId,
  };
}
